/**
 * The scoring weights of a subsector are shown as percentages with one
 * decimal and stored as fractions that sum to 1, the rule of the unifier
 * (`scoring_weight_repository.py`, per subsector). These helpers keep the
 * total at 100 % while one weight changes.
 */

/** The weights the unifier uses for a subsector without stored weights. */
export const DEFAULT_WEIGHT_FRACTIONS: Readonly<Record<number, number>> = {
  1: 0.35,
  2: 0.15,
  3: 0.1,
  4: 0.1,
  5: 0.05,
  6: 0.05,
  7: 0.05,
  8: 0.05,
  9: 0.05,
  10: 0.05,
};

const sum = (values: number[]) => values.reduce((acc, x) => acc + x, 0);
const round1 = (value: number) => Math.round(value * 10) / 10;
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** The total of the percentages, rounded to one decimal. */
export const weightTotal = (percents: number[]) => round1(sum(percents));

/** Saving is possible at a total of exactly 100 % only. */
export const isCompleteTotal = (percents: number[]) =>
  Math.abs(sum(percents) - 100) < 0.05;

/**
 * Splits `target` (in percent) over the values by their shares in steps of
 * 0.1, so the parts add up to `target` exactly (largest remainder). Values
 * that are all 0 get equal parts.
 */
export const distributeByShare = (values: number[], target: number) => {
  if (!values.length) {
    return [];
  }
  const base = sum(values) > 0 ? values : values.map(() => 1);
  const baseSum = sum(base);
  const tenths = Math.max(0, Math.round(target * 10));
  const raw = base.map((value) => (value / baseSum) * tenths);
  const parts = raw.map(Math.floor);
  let remaining = tenths - sum(parts);
  raw
    .map((value, index) => ({ index, rest: value - parts[index] }))
    .sort((a, b) => b.rest - a.rest || a.index - b.index)
    .forEach(({ index }) => {
      if (remaining > 0) {
        parts[index] += 1;
        remaining -= 1;
      }
    });
  return parts.map((part) => part / 10);
};

/**
 * The stored weights as percentages. Fractions are scaled by 100, old
 * percentages are kept; a subsector without weights shows the defaults. A
 * stored total of 1 stays exactly 100 % after rounding to one decimal.
 */
export const toPercents = (
  stored: number[],
  criterionIds: number[]
): { percents: number[]; defaults: boolean } => {
  const values = stored.map((value) => (Number.isFinite(value) ? value : 0));
  if (sum(values) === 0) {
    return {
      percents: criterionIds.map((id) =>
        round1((DEFAULT_WEIGHT_FRACTIONS[id] ?? 0) * 100)
      ),
      defaults: true,
    };
  }
  const scale = values.every((value) => value <= 1) ? 100 : 1;
  const scaled = values.map((value) => value * scale);
  const percents =
    Math.abs(sum(scaled) - 100) < 0.05
      ? distributeByShare(scaled, 100)
      : scaled.map(round1);
  return { percents, defaults: false };
};

/** Sets one weight and leaves the others as they are. */
export const setWeight = (percents: number[], index: number, value: number) =>
  percents.map((current, i) =>
    i === index ? clamp(round1(value), 0, 100) : current
  );

/**
 * Sets one weight and spreads the rest of 100 % over the other weights that
 * are not locked, in proportion to their current values. The weight cannot
 * grow beyond what the locked weights leave.
 */
export const rebalance = (
  percents: number[],
  index: number,
  value: number,
  locked: boolean[]
) => {
  const free = percents
    .map((_, i) => i)
    .filter((i) => i !== index && !locked[i]);
  const lockedSum = sum(percents.filter((_, i) => i !== index && locked[i]));
  const max = free.length
    ? Math.max(0, round1(100 - lockedSum))
    : percents[index];
  const next = clamp(round1(value), 0, max);
  const result = [...percents];
  result[index] = next;
  if (free.length) {
    const parts = distributeByShare(
      free.map((i) => percents[i]),
      round1(100 - lockedSum - next)
    );
    free.forEach((i, k) => (result[i] = parts[k]));
  }
  return result;
};

/** Equal parts of what the locked weights leave for the other weights. */
export const distributeEvenly = (percents: number[], locked: boolean[]) => {
  const free = percents.map((_, i) => i).filter((i) => !locked[i]);
  const lockedSum = sum(percents.filter((_, i) => locked[i]));
  const parts = distributeByShare(
    free.map(() => 1),
    round1(100 - lockedSum)
  );
  const result = [...percents];
  free.forEach((i, k) => (result[i] = parts[k]));
  return result;
};

/**
 * The percentages as fractions to store. The largest weight takes the
 * rounding difference, so the fractions sum to 1 within 1e-9.
 */
export const toFractions = (percents: number[]) => {
  const fractions = percents.map((value) => value / 100);
  if (!fractions.length) {
    return fractions;
  }
  const largest = fractions.indexOf(Math.max(...fractions));
  const others = sum(fractions.filter((_, i) => i !== largest));
  fractions[largest] = 1 - others;
  return fractions;
};
